// src/components/ui/QuotasManager.js
import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import TargetIcon from '../svgIcons/targetIcon';
import QuotasMonitorer from './QuotasMonitorer'
import QuotasCreator from './QuotasCreator';
import API_CONFIG from '../../config/api';

const BASE_URL = API_CONFIG.BASE_URL;

function QuotasManager({ surveyId }) {
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('monitor');
	const [surveyInfos, setSurveyInfos] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	
	const { authenticatedFetch } = useAuthenticatedApi();

	const handleBackClick = () => {
		// Navigate back to the previous page (dashboard)
		navigate(-1);
	};

	useEffect(() => {
		getSurveyInfo();
	}, [surveyId]);

	const getSurveyInfo = async () => {
		setIsLoading(true);
		setError(null); // Clear previous errors

		try {
			const endpoint = `${BASE_URL}/surveys/survey-info-by-id/${surveyId}`;
			const result = await authenticatedFetch(endpoint, {}, "GET");
			if (result.status === 404) {
				navigate('/404', { state: { message: `Survey with ID "${surveyId}" not found.`} });
				return;
			}
			if (result.status === 401) {
				// Unauthorized access (participant token invalid or survey inactive)
				setError("You are not authorized to access this survey or you are not authenticated");
				setSurveyInfos(null); // Clear survey data on unauthorized
				return;
			}
			if (!result.ok) {
				// Handle other non-2xx responses
				let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Response body isn't JSON? Ignore and keep generic error
				}
				console.error("QuotasManager API Error:", errorMessage, result);
				setError(errorMessage);
				setSurveyInfos(null); // Clear model on error
				return;
			}

			const data = await result.json();
			if (data) {
				setSurveyInfos(data);
			} else {
				console.error("API returned empty or invalid data structure:", data);
				setError("Survey data is empty or malformed.");
				setSurveyInfos([]);
			}
		} catch (err) {
			console.error("Network or unexpected error while fetching survey:", err);
			setError("Failed to load survey. Please check your connection and try again.");
			setSurveyInfos([]);
		} finally {
			setIsLoading(false);
		}
	};

	const formatDate = (dateString) => {
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			});
		} catch (error) {
			return dateString;
		}
	};

	const truncateDescription = (description, maxLength = 128) => {
		if (!description) return '';
		if (description.length <= maxLength) return description;
		return description.substring(0, maxLength) + '...';
	};

	return (
		<div className="min-h-screen bg-gray-50 p-4 animate-in slide-in-from-right duration-300">
			<div className="max-w-7xl mx-auto">
				{/* Header Section */}
				<div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
					<div className="flex items-center gap-4">
						<button
							onClick={handleBackClick}
							className="flex items-center justify-center w-10 h-10 rounded-full border hover:bg-gray-100 transition-colors"
							title="Go back"
						>
							<ArrowLeft className="h-5 w-5 text-gray-600" />
						</button>
						
						<div className="flex-1">
							<h1 className="text-2xl font-bold text-gray-900 flex items-center justify-left">
								<TargetIcon className='w-8 h-8 mr-2'/>
								Manage Quotas
							</h1>
							{isLoading ? (
								<div className="mt-2 space-y-2">
									<div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
									<div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
								</div>
							) : surveyInfos && !error ? (
								<div className="mt-2 space-y-1">
									<p className="text-lg font-medium text-gray-800">
										{surveyInfos.title}
									</p>
									{surveyInfos.description && (
										<p className="text-sm text-gray-600">
											{truncateDescription(surveyInfos.description)}
										</p>
									)}
									<div className="flex items-center gap-4 text-sm text-gray-500">
										<span>Survey ID: {surveyInfos.survey_id}</span>
										<span>Created: {formatDate(surveyInfos.created_at)}</span>
									</div>
								</div>
							) : (
								<p className="text-sm text-gray-500 mt-1">
									Survey ID: {surveyId}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Main Content Area - Tabbed Interface */}
				<div className="bg-white rounded-lg shadow-sm border overflow-hidden">
					{error ? (
						// Error State
						<div className="p-6">
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<div className="flex">
									<div className="flex-shrink-0">
										<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
											<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
										</svg>
									</div>
									<div className="ml-3">
										<h3 className="text-sm font-medium text-red-800">
											Error Loading Survey
										</h3>
										<p className="mt-1 text-sm text-red-700">
											{error}
										</p>
									</div>
								</div>
							</div>
						</div>
					) : (
						<>
							{/* Tab Navigation */}
							<div className="border-b border-gray-200">
								<nav className="flex space-x-8" aria-label="Tabs">
									<button
										onClick={() => setActiveTab('monitor')}
										className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'monitor'
												? 'text-gray-800 text-blue-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Monitor Quotas
									</button>
									<button
										onClick={() => setActiveTab('create')}
										className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
											activeTab === 'create'
												? 'text-gray-800 text-blue-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Administrate Quotas
									</button>
								</nav>
							</div>

							{/* Tab Content */}
							<div className="p-6">
								{activeTab === 'monitor' && (
									<QuotasMonitorer surveyId={surveyId} />
								)}
								{activeTab === 'create' && (
									<QuotasCreator surveyId={surveyId} />
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default QuotasManager;